import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Typography, Grid } from '@mui/material';
import Link from '@mui/material/Link';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function HomePage({ data }) {
  const event = data || {};
  const isCompleted = event.status === 'completed';
  const photos = event.photos ? Object.values(event.photos) : [];

  // Lightbox state
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Home | OneT | Leading DevSecOps Service Provider</title>
      </Helmet>

      <Grid
        sx={{
          top: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        {/* Background */}
        <Grid
  sx={{
    zIndex: 0,
    position: 'fixed',        // ⭐ stays behind everything, fills whole screen
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',          // ⭐ FULL SCREEN ALWAYS
    backgroundColor: '#1b0049',
    backgroundImage: `url(${event.event_bg_fullPath || ''})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  }}
/>

        {/* Overlay */}
        <Grid
  sx={{
    zIndex: 1,
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    backgroundColor: '#000',
    opacity: 0.7,
  }}
/>

        {/* Content */}
        <Grid
          sx={{
            minHeight: '83vh',    // ⬅ allows growth instead of fixed height
            maxWidth: 1200,
            zIndex: 2,
            marginTop: 12,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            pt: 7,
            pb: 10,
            backgroundColor: 'transparent',
          }}
        >
          <Typography sx={{ mt: -1, fontSize: 20, color: 'white' }}>{event.subtitle}</Typography>

          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: 40,
              color: 'white',
              textTransform: 'uppercase',
            }}
          >
            {event.title}
          </Typography>

          <Typography
            sx={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              maxWidth: 500,
              fontWeight: 300,
              fontSize: 14,
              color: 'white',
              opacity: 0.8,
            }}
          >
            {event.description}
          </Typography>

          {/* Dates */}
          <Grid container sx={{ mt: 3 }}>
            <Grid item md={3} xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
              <img src="/assets/calendaricon.png" alt="calendaricon" height={20} />
              <Typography sx={{ pl: 1, fontSize: 14, color: 'white' }}>
                {event.start_date
                  ? `${moment.utc(event.start_date).add(8, 'hours').format('DD/MM/YYYY')} - ${moment
                      .utc(event.end_date)
                      .add(8, 'hours')
                      .format('DD/MM/YYYY')}`
                  : ''}
              </Typography>
            </Grid>

            <Grid item md={9} xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
              <img src="/assets/timeicon.png" alt="timeicon" height={20} />
              <Typography sx={{ pl: 1, fontSize: 14, color: 'white' }}>
                {event.start_time
                  ? `${moment.utc(event.start_time).add(8, 'hours').format('hh:mm a')} - ${moment
                      .utc(event.end_time)
                      .add(8, 'hours')
                      .format('hh:mm a')}`
                  : ''}
              </Typography>
            </Grid>
          </Grid>

          {/* Venue */}
          <Grid container sx={{ mt: 1 }}>
            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
              <img src="/assets/venueicon.png" alt="venueicon" height={20} />
              <Typography sx={{ pl: 1, fontSize: 14, color: 'white' }}>{event.venue}</Typography>
            </Grid>
          </Grid>

          {/* Conditional: Register or Gallery */}
          {!isCompleted ? (
            <Link href={`/register_user/${event.prefix}`} style={{ textDecoration: 'none' }}>
              <Grid sx={{ mt: 10, cursor: 'pointer' }}>
                <img src="/assets/registerbtn.png" alt="register button" width={120} />
              </Grid>
            </Link>
          ) : (
            <Grid
              container
              spacing={2}
              sx={{
                mt: 5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {photos.length > 0 ? (
                photos.map((photo, index) => (
                  <Grid item key={photo.name} xs={6} sm={4} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <img
                      src={photo.url}
                      alt={photo.name}
                      onClick={() => openLightbox(index)}
                      style={{
                        width: '100%',
                        maxWidth: 200,
                        height: 160,
                        borderRadius: 8,
                        objectFit: 'cover',
                        cursor: 'zoom-in',
                        transition: 'transform 0.2s ease',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                      onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    />
                  </Grid>
                ))
              ) : (
                <Typography variant="body2" color="white" sx={{ mt: 3, opacity: 0.8 }}>
                  📸 No photos uploaded yet.
                </Typography>
              )}
            </Grid>
          )}

          {/* Lightbox Modal */}
          {isOpen && (
            <Lightbox
              mainSrc={photos[photoIndex].url}
              nextSrc={photos[(photoIndex + 1) % photos.length]?.url}
              prevSrc={photos[(photoIndex + photos.length - 1) % photos.length]?.url}
              onCloseRequest={() => setIsOpen(false)}
              onMovePrevRequest={() => setPhotoIndex((photoIndex + photos.length - 1) % photos.length)}
              onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % photos.length)}
              imageCaption={photos[photoIndex].name}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
